import { buildBradleySourcesFromMessage } from '../utils/bradley-sources.js'
import { BRADLEY_ANALYSE_DATA_TOOL_LABEL } from './bradley-analytics.constants.js'

/**
 * Privacy-safe metadata for Bradley file-related Amplitude events (no raw filenames).
 */

/** Map selected UI tools to the analytics taxonomy values. */
export function bradleyToolsSelectedForAnalytics(selectedTools) {
  const tools = Array.isArray(selectedTools) ? selectedTools : []
  const out = []
  if (tools.includes(BRADLEY_ANALYSE_DATA_TOOL_LABEL))
    out.push('dss_360_analysis')
  return out
}

/**
 * @param {number} byteLength
 * @returns {string} Coarse size bucket label for Amplitude (low cardinality).
 */
export function bradleyFileSizeBucket(byteLength) {
  const n = typeof byteLength === 'number' && Number.isFinite(byteLength) ? byteLength : 0
  if (n < 1024 * 1024)
    return 'lt_1mb'
  if (n < 10 * 1024 * 1024)
    return '1mb_to_10mb'
  return 'gte_10mb'
}

/**
 * @param {File | { name?: string, size?: number }} file
 * @returns {{ file_extension: string, file_size_bytes: number, size_bucket: string }} Extension and size metadata without raw basename paths.
 */
export function bradleyFileTelemetryMeta(file) {
  const name = file && typeof file.name === 'string' ? file.name : ''
  const fileExtension = name.includes('.')
    ? name.slice(name.lastIndexOf('.') + 1).toLowerCase().slice(0, 24)
    : 'none'
  const bytes = typeof file?.size === 'number' && Number.isFinite(file.size) ? file.size : 0
  return {
    file_extension: fileExtension || 'none',
    file_size_bytes: bytes,
    size_bucket: bradleyFileSizeBucket(bytes),
  }
}

/**
 * Extension only from a path/filename string (no directory or base name sent to analytics).
 * @param {string} [fileName]
 * @returns {string} Lowercase extension or `none` / `unknown`.
 */
export function bradleyExtensionFromFileName(fileName) {
  if (!fileName || typeof fileName !== 'string')
    return 'unknown'
  const dotIndex = fileName.lastIndexOf('.')
  return dotIndex >= 0 ? fileName.slice(dotIndex + 1).toLowerCase().slice(0, 24) : 'none'
}

/**
 * Map upload failures to the `error_type` expected by `file_upload_failed`.
 * @param {unknown} error
 * @returns {'network' | 'file_size_limit' | 'unsupported_format' | 'unknown'} Normalized upload error type.
 */
export function bradleyUploadErrorAnalyticsType(error) {
  const message = error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
    ? error.message
    : ''
  if (/network|fetch|failed to fetch/i.test(message))
    return 'network'
  if (/size|limit|too large|413/i.test(message))
    return 'file_size_limit'
  if (/type|format|unsupported|invalid/i.test(message))
    return 'unsupported_format'
  return 'unknown'
}

/**
 * Build a short, privacy-safe error category for analytics.
 * @param {unknown} error
 * @returns {string} Short category built from error name and optional code.
 */
export function bradleyAnalyticsErrorCategory(error) {
  const name = error && typeof error === 'object' && 'name' in error && typeof error.name === 'string'
    ? error.name.slice(0, 40)
    : 'Error'
  const rawCode = error && typeof error === 'object' && 'code' in error ? error.code : undefined
  const code = typeof rawCode === 'string' || typeof rawCode === 'number' || typeof rawCode === 'boolean' || typeof rawCode === 'bigint'
    ? String(rawCode).slice(0, 24)
    : ''
  return code ? `${name}_${code}`.slice(0, 64) : name.slice(0, 64)
}

/**
 * @param {object | null | undefined} message
 * @param {Array<object>} conversationMessages
 * @param {string | number | null | undefined} conversationId
 */
export function bradleySourcesModalAnalyticsPayload(message, conversationMessages = [], conversationId = null) {
  const sources = buildBradleySourcesFromMessage(message, conversationMessages)

  return {
    surface: 'sources_modal',
    conversation_id: conversationId == null ? null : String(conversationId),
    message_id: message?.message_id ?? null,
    sources_count: sources.length,
    source_types: sources.map(source => source.type),
  }
}
